// src/pages/parcours-client/PaymentResultSuccess.jsx
import React, { useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Footer from "../../components/Footer";
import Navbar from "../Navbar";

const CLIENT_PORTAL_URL = import.meta?.env?.VITE_CLIENT_PORTAL_URL || "/login";
const BRAND = "#449F2D";
const BRAND_HOVER = "#388624";

export default function PaymentResultSuccess() {
  const { t } = useTranslation("parcours-client/PaymentResultSuccess");
  const navigate = useNavigate();
  const location = useLocation();
  const isNarrow = useMediaQuery("(max-width:800px)");

  const email = useMemo(() => {
    const fromState = location.state?.email || null;
    if (fromState) return fromState;
    try {
      const qs = new URLSearchParams(location.search);
      const fromQS = qs.get("email");
      if (fromQS) return fromQS;
    } catch {}
    try {
      const ls = localStorage.getItem("syb:lastPaymentEmail");
      if (ls) return ls || null;
    } catch {}
    try {
      const raw = sessionStorage.getItem("syb:lastPaymentPayload");
      if (!raw) return null;
      return JSON.parse(raw)?.payload?.subscriber?.email || null;
    } catch {
      return null;
    }
  }, [location.state, location.search]);

  useEffect(() => {
    if (email) {
      try { localStorage.removeItem("syb:lastPaymentEmail"); } catch {}
    }
  }, [email]);

  const sentTo = email ? t("success.sentTo", { email }) : "";

  return (
    <>
      <Navbar />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          bgcolor: "#fff", // fond blanc partout
        }}
      >
        {/* espace sous la navbar */}
        <Box sx={{ height: 90 }} />

        <Box
          sx={{
            flex: "1 0 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: 2,
            py: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: { xs: "92vw", sm: "100%" },
              maxWidth: 560,
              px: { xs: 3, sm: 5 },
              py: { xs: 4, sm: 5 },
              borderRadius: 3,
              textAlign: "center",
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
            }}
          >
            {/* Badge succès */}
            <Box
              sx={{
                width: 86,
                height: 86,
                borderRadius: "50%",
                mx: "auto",
                mb: 2,
                display: "grid",
                placeItems: "center",
                backgroundColor: "rgba(68,159,45,0.12)",
                border: "1px solid rgba(68,159,45,0.25)",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: BRAND }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 900, color: "#204B15", mb: 1 }}>
              {t("success.title")}
            </Typography>

            <Typography variant="body1" sx={{ color: "#4b5563", mb: 1.5 }}>
              {t("success.body")}
            </Typography>

            {email && (
              <Typography variant="body2" sx={{ mb: 3, color: "#6b7280" }}>
                {sentTo}
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ flexWrap: "wrap", rowGap: 1.5 }}
            >
              <Button
                variant="contained"
                disableElevation
                href={CLIENT_PORTAL_URL}
                sx={{
                  bgcolor: BRAND,
                  color: "#fff",
                  borderRadius: 999,
                  px: 3,
                  py: 1.1,
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": { bgcolor: BRAND_HOVER, color: "#fff" },
                }}
              >
                {t("success.gotoPortal")}
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate("/garanties")}
                sx={{
                  borderColor: BRAND,
                  color: BRAND,
                  borderRadius: 999,
                  px: 3,
                  py: 1.1,
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: BRAND_HOVER,
                    backgroundColor: "rgba(68,159,45,0.06)",
                  },
                }}
              >
                {t("success.goHome")}
              </Button>
            </Stack>
          </Paper>
        </Box>

        <Footer />
      </Box>
    </>
  );
}